import React from "react";

// Shared by the customer menu and the admin products table so the wording can
// never drift between what ops sees and what customers see.
export const NUTRITION_LABEL = { protein: "Protein rich", fiber: "Fiber rich" };

// `tier` is computed server-side in Helper/nutritionTier.js — never derived
// here, or the two would disagree the moment a threshold changes.
export const NUTRITION_TIER_LABEL = {
  high: { protein: "High protein", fiber: "High fiber" },
  good: { protein: "Good source of protein", fiber: "Good source of fiber" },
};

// Base field holds the single value or the low end; *Max holds the high end.
const GRAM_FIELD = { protein: "proteinGrams", fiber: "fiberGrams" };

/**
 * "7" -> "7g" | "6" + "8" -> "6–8g" | nothing filled in -> null.
 * A max equal to the min collapses back to a single value rather than "7–7g".
 */
export const formatNutrient = (min, max, suffix = "g") => {
  if (min === null || min === undefined || min === "") return null;
  const hasRange =
    max !== null &&
    max !== undefined &&
    max !== "" &&
    Number(max) !== Number(min);
  return hasRange ? `${min}–${max}${suffix}` : `${min}${suffix}`;
};

// Short noun for the pill — the pill states a value ("4.2g Protein"), so it
// reads as a fact rather than the claim NUTRITION_LABEL makes.
export const NUTRIENT_NOUN = { protein: "Protein", fiber: "Fiber" };

const LeafIcon = ({ size = 13 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path d="M20 3s-9.5-1.2-14.2 3.5C2.4 9.9 2.6 15 5.4 17.9l-2 2a1 1 0 1 0 1.4 1.4l2-2C9.7 21.9 14.8 22 18.2 18.6 22.9 13.9 20 3 20 3zm-3.6 4.7L9.1 15a1 1 0 1 0 1.4 1.4l7.3-7.3c.3 3-.2 6.5-2 8.3-2.6 2.6-6.7 2.4-9 .1s-2.5-6.4.1-9c2.4-2.4 8-2.9 12-2.6-.5.5-1.3 1.2-2.5 1.8z" />
  </svg>
);

/**
 * Compact single-line pill for a food card: leaf + "4.2g Protein".
 *
 * Visibility depends on the highlighted nutrient having a value, NOT on the
 * tier: stating "6–8g Protein" is a fact, so it can always show. The tier only
 * controls emphasis — "high" renders filled so it still reads louder while
 * scrolling, which is the distinction between a protein-rich and an average
 * dish.
 */
export const NutritionBadge = ({ item, showIcon = true }) => {
  const highlight = item?.nutritionHighlight;
  if (!highlight) return null;

  const gramField = GRAM_FIELD[highlight];
  const value = formatNutrient(item?.[gramField], item?.[`${gramField}Max`]);
  if (!value) return null;

  const isHigh = item?.nutritionTier === "high";

  return (
    <span
      title={
        NUTRITION_TIER_LABEL[item?.nutritionTier]?.[highlight] || undefined
      }
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 9px",
        borderRadius: 999,
        fontSize: "0.7rem",
        lineHeight: 1.3,
        whiteSpace: "nowrap",
        background: isHigh ? "#1D9E75" : "#EFF7EE",
        color: isHigh ? "#fff" : "#2E7D32",
        border: `1px solid ${isHigh ? "#1D9E75" : "#CDE8C9"}`,
      }}
    >
      {showIcon && <LeafIcon />}
      <span style={{ fontWeight: 700 }}>{value}</span>
      <span style={{ fontWeight: 500, opacity: isHigh ? 0.95 : 0.85 }}>
        {NUTRIENT_NOUN[highlight]}
      </span>
    </span>
  );
};

/**
 * Full per-nutrient breakdown for the product detail view. Skips anything not
 * filled in rather than showing zeros.
 */
export const NutritionBreakdown = ({ item }) => {
  const rows = [
    ["Protein", formatNutrient(item?.proteinGrams, item?.proteinGramsMax)],
    ["Fiber", formatNutrient(item?.fiberGrams, item?.fiberGramsMax)],
    ["Carbs", formatNutrient(item?.carbsGrams, item?.carbsGramsMax)],
    ["Fat", formatNutrient(item?.fatGrams, item?.fatGramsMax)],
    ["Calories", formatNutrient(item?.calories, item?.caloriesMax, " kcal")],
  ].filter(([, value]) => value);

  if (rows.length === 0) return null;

  return (
    <div style={{ margin: "10px 0" }}>
      <div
        style={{
          fontSize: "0.72rem",
          color: "#8a8a8a",
          textTransform: "uppercase",
          letterSpacing: 0.4,
          marginBottom: 4,
        }}
      >
        Nutrition (per pack)
      </div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        {rows.map(([label, value]) => (
          <div key={label} style={{ fontSize: "0.8rem", color: "#444" }}>
            <span style={{ fontWeight: 600 }}>{value}</span>{" "}
            <span style={{ color: "#8a8a8a" }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NutritionBadge;
